import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Loader2, Check, X, Clock, ShieldCheck, Building2, RotateCcw, ShieldX, Megaphone, Send, CheckCheck, Phone, Wallet, LogIn, KeyRound, Eye, EyeOff, Video, VideoOff, Pencil, Trash2, UserPlus, Download, ExternalLink, Receipt, CreditCard } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PricingBadge, PricingOverrideDialog } from "@/components/PricingOverride";
import { SendInvoiceDialog } from "@/components/SendInvoice";
import { ChargeClientDialog } from "@/components/ChargeClient";

const STATUS = {
  pending: { label: "Pending", cls: "bg-amber-500/15 text-amber-400 border-amber-500/30", dot: "bg-amber-400" },
  approved: { label: "Approved", cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", dot: "bg-emerald-400" },
  rejected: { label: "Rejected", cls: "bg-red-500/15 text-red-400 border-red-500/30", dot: "bg-red-400" },
};

const PLANS = {
  "basic-broadcaster": { label: "Basic Broadcaster", price: "$49.95" },
  "media-ministry-hub": { label: "Media Ministry Hub", price: "$59.95" },
  "premium-services": { label: "Premium Services", price: "$89.95" },
  "ascend-ultra": { label: "Ascend Ultra", price: "$99.95" },
};

const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }); } catch { return iso; } };

const BAR_COLORS = ["#F59E0B", "#FBBF24", "#D97706", "#FCD34D", "#B45309", "#FDE68A", "#92400E"];

function SignupInsights({ users }) {
  const counts = {};
  users.forEach((u) => {
    if (u.role === "admin") return;
    const src = (u.heard_from || "").trim() || "Not specified";
    counts[src] = (counts[src] || 0) + 1;
  });
  const data = Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) return null;
  const top = data[0];

  return (
    <div data-testid="signup-insights" className="bg-card border border-gray-800 rounded-2xl p-5 mb-6">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-4">
        <div>
          <h2 className="font-display text-base font-semibold flex items-center gap-2"><Megaphone className="h-4 w-4 text-amber-400" /> Where signups come from</h2>
          <p className="text-xs text-gray-500 mt-0.5">Based on the "How did you hear about us?" answer from {total} signup{total === 1 ? "" : "s"}.</p>
        </div>
        {top && <span className="text-xs text-gray-400">Top source: <span className="text-amber-400 font-semibold">{top.name}</span> ({top.value})</span>}
      </div>
      <ResponsiveContainer width="100%" height={Math.max(160, data.length * 42)}>
        <BarChart data={data} layout="vertical" margin={{ left: 8, right: 24, top: 4, bottom: 4 }}>
          <CartesianGrid horizontal={false} stroke="#1f2937" />
          <XAxis type="number" allowDecimals={false} stroke="#6b7280" fontSize={12} />
          <YAxis type="category" dataKey="name" width={130} stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} />
          <Tooltip cursor={{ fill: "rgba(245,158,11,0.08)" }} contentStyle={{ background: "#111827", border: "1px solid #374151", borderRadius: 8, color: "#fff" }} formatter={(v) => [`${v} signup${v === 1 ? "" : "s"}`, "Count"]} />
          <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={22}>
            {data.map((_, i) => <Cell key={i} fill={BAR_COLORS[i % BAR_COLORS.length]} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default function AdminUsers() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [busy, setBusy] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);
  const [planBusy, setPlanBusy] = useState(null);
  const [pwUser, setPwUser] = useState(null);
  const [newPw, setNewPw] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [pwSaving, setPwSaving] = useState(false);
  const [recBusy, setRecBusy] = useState(null);
  const [priceUser, setPriceUser] = useState(null);
  const [invUser, setInvUser] = useState(null);
  const [chargeUser, setChargeUser] = useState(null);

  const toggleRecording = async (u) => {
    const next = !(u.archive_streams !== false);
    setRecBusy(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/recording`, { enabled: next });
      setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, archive_streams: next } : x)));
      toast.success(`Stream archiving ${next ? "enabled" : "disabled"} for ${u.name || u.email}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update archiving"); }
    finally { setRecBusy(null); }
  };

  const resetPassword = async () => {
    if (newPw.trim().length < 8) { toast.error("Password must be at least 8 characters"); return; }
    setPwSaving(true);
    try {
      await api.post(`/admin/users/${pwUser.user_id}/password`, { password: newPw });
      toast.success(`Password updated for ${pwUser.name || pwUser.email}`);
      setPwUser(null); setNewPw(""); setShowPw(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't update password"); }
    finally { setPwSaving(false); }
  };

  const setPlan = async (u, plan) => {    if ((u.plan || "") === plan) return;
    setPlanBusy(u.user_id);
    try {
      const res = await api.post(`/admin/users/${u.user_id}/plan`, { plan });
      setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, plan, pending_payment_plan: "" } : x)));
      toast.success(`${u.name || u.email} set to ${res.data.plan_name}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to update plan"); }
    finally { setPlanBusy(null); }
  };

  const accessAccount = async (u) => {
    try {
      await api.post(`/admin/users/${u.user_id}/impersonate`);
      toast.success(`Accessing ${u.name || u.email}'s account…`);
      window.location.href = "/streams";
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't access this account"); }
  };

  const load = async () => {
    try { setUsers((await api.get("/admin/users")).data); }
    catch (e) { toast.error("Failed to load accounts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (u, status) => {
    setBusy(u.user_id);
    try {
      await api.post(`/admin/users/${u.user_id}/status`, { status });
      setUsers((list) => list.map((x) => (x.user_id === u.user_id ? { ...x, status } : x)));
      toast.success(`${u.name || u.email} ${status === "approved" ? "approved" : status === "rejected" ? "rejected" : "set to pending"}`);
    } catch (e) { toast.error("Action failed"); }
    finally { setBusy(null); }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const clearSel = () => setSelected(new Set());

  const bulkAction = async (status) => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      const res = await api.post("/admin/users/bulk-status", { user_ids: ids, status });
      setUsers((list) => list.map((x) => (selected.has(x.user_id) && x.role !== "admin" ? { ...x, status } : x)));
      toast.success(`${res.data.updated} account${res.data.updated === 1 ? "" : "s"} ${status === "approved" ? "approved" : "rejected"}`);
      clearSel();
    } catch (e) { toast.error("Bulk action failed"); }
    finally { setBulkBusy(false); }
  };

  const sendDigest = async () => {
    setDigestBusy(true);
    try {
      const res = await api.post("/admin/send-digest-now");
      toast.success(`Weekly digest queued to ${res.data.recipient || "the team inbox"}`);
    } catch (e) { toast.error("Couldn't send digest"); }
    finally { setDigestBusy(false); }
  };

  const exportCsv = () => {
    const clients = users.filter((u) => u.role !== "admin");
    if (clients.length === 0) { toast.error("No clients to export"); return; }
    const COLS = [
      ["name", "Name"], ["email", "Email"], ["organization", "Organization"], ["phone", "Phone"],
      ["address", "Address"], ["city", "City"], ["state", "State"], ["zip", "Zip"],
      ["plan", "Plan"], ["status", "Status"], ["heard_from", "Heard From"],
      ["date_activated", "Date Activated"], ["created_at", "Joined"],
    ];
    const esc = (v) => { const s = v == null ? "" : String(v); return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s; };
    const rows = [COLS.map(([, h]) => h).join(",")];
    clients.forEach((u) => rows.push(COLS.map(([k]) => esc(k === "plan" ? (PLANS[u.plan]?.label || "Free Trial") : u[k])).join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `clients-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exported ${clients.length} client${clients.length === 1 ? "" : "s"}`);
  };

  const now = Date.now();
  const EMPTY_FORM = { email: "", password: "", name: "", organization: "", phone: "", address: "", city: "", state: "", zip: "", date_activated: "", plan: "" };
  const [clientOpen, setClientOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [savingClient, setSavingClient] = useState(false);
  const [delUser, setDelUser] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const toDateInput = (iso) => { if (!iso) return ""; try { return new Date(iso).toISOString().slice(0, 10); } catch { return ""; } };
  const openCreate = () => { setEditUser(null); setForm(EMPTY_FORM); setClientOpen(true); };
  const openEdit = (u) => {
    setEditUser(u);
    setForm({ email: u.email || "", password: "", name: u.name || "", organization: u.organization || "", phone: u.phone || "", address: u.address || "", city: u.city || "", state: u.state || "", zip: u.zip || "", date_activated: toDateInput(u.date_activated), plan: u.plan || "" });
    setClientOpen(true);
  };
  const saveClient = async () => {
    setSavingClient(true);
    try {
      if (editUser) {
        const body = { name: form.name, organization: form.organization, phone: form.phone, address: form.address, city: form.city, state: form.state, zip: form.zip, date_activated: form.date_activated };
        await api.patch(`/admin/users/${editUser.user_id}/profile`, body);
        setUsers((list) => list.map((x) => (x.user_id === editUser.user_id ? { ...x, ...body } : x)));
        toast.success("Client updated");
      } else {
        await api.post("/admin/users", { ...form });
        await load();
        toast.success("Client created");
      }
      setClientOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSavingClient(false); }
  };
  const deleteClient = async () => {
    if (!delUser) return;
    setDeleting(true);
    try {
      await api.delete(`/admin/users/${delUser.user_id}`);
      setUsers((list) => list.filter((x) => x.user_id !== delUser.user_id));
      toast.success(`${delUser.name || delUser.email} deleted`);
      setDelUser(null);
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
    finally { setDeleting(false); }
  };

  const trialState = (u) => {
    if (!u.trial_ends_at) return null;
    const end = new Date(u.trial_ends_at).getTime();
    if (isNaN(end)) return null;
    return end > now ? "trial" : "expired";
  };
  const filtered = users.filter((u) => {
    if (filter === "all") return true;
    if (filter === "trial" || filter === "expired") return trialState(u) === filter;
    return u.status === filter;
  });
  const pendingCount = users.filter((u) => u.status === "pending").length;
  const selectableRows = filtered.filter((u) => u.role !== "admin");
  const allSelected = selectableRows.length > 0 && selectableRows.every((u) => selected.has(u.user_id));
  const toggleSelectAll = () => {
    if (allSelected) { clearSel(); }
    else { setSelected(new Set(selectableRows.map((u) => u.user_id))); }
  };
  const FILTERS = [
    { key: "all", label: "All" },
    { key: "pending", label: "Pending" },
    { key: "approved", label: "Approved" },
    { key: "rejected", label: "Rejected" },
    { key: "trial", label: "In Trial" },
    { key: "expired", label: "Trial Expired" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-[1600px] mx-auto">
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight flex items-center gap-3">
            Clients &amp; Accounts
            {pendingCount > 0 && <span className="text-xs font-semibold px-2 py-1 rounded-md bg-amber-500/15 text-amber-400 border border-amber-500/30">{pendingCount} pending</span>}
          </h1>
          <p className="text-gray-400 mt-1.5 text-sm">Review and approve customer accounts. New sign-ups stay pending until you approve them here.</p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="add-client-btn" onClick={openCreate} className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-semibold gold-gradient text-black hover:brightness-110 transition-all">
            <UserPlus className="h-4 w-4" /> Add Client
          </button>
          <button data-testid="export-clients-btn" onClick={exportCsv} className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-medium border border-gray-700 text-gray-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors">
            <Download className="h-4 w-4" /> Export Clients
          </button>
          <button data-testid="send-digest-now" onClick={sendDigest} disabled={digestBusy} className="inline-flex items-center gap-2 px-4 h-11 rounded-lg text-sm font-medium border border-gray-700 text-gray-200 hover:border-amber-500/50 hover:text-amber-300 transition-colors disabled:opacity-60">
            {digestBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send weekly digest now
          </button>
        </div>
      </div>

      {!loading && users.length > 0 && <SignupInsights users={users} />}

      <div className="flex flex-wrap gap-2 mb-5">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`user-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={"px-4 h-10 rounded-lg text-sm font-medium transition-colors border " + (filter === f.key ? "bg-amber-500/15 text-amber-400 border-amber-500/40" : "bg-card text-gray-400 border-gray-800 hover:text-white")}>
            {f.label}
          </button>
        ))}
      </div>

      {selected.size > 0 && (
        <div data-testid="bulk-action-bar" className="mb-4 flex items-center gap-3 flex-wrap rounded-xl border border-amber-500/30 bg-amber-500/[0.06] px-4 py-3">
          <span className="text-sm text-amber-200 font-medium">{selected.size} selected</span>
          <div className="flex items-center gap-2 ml-auto">
            {bulkBusy ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : (
              <>
                <button data-testid="bulk-approve" onClick={() => bulkAction("approved")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-medium transition-colors"><CheckCheck className="h-3.5 w-3.5" /> Approve selected</button>
                <button data-testid="bulk-reject" onClick={() => bulkAction("rejected")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-xs font-medium transition-colors"><X className="h-3.5 w-3.5" /> Reject selected</button>
                <button data-testid="bulk-clear" onClick={clearSel} className="text-xs text-gray-400 hover:text-white px-2">Clear</button>
              </>
            )}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-32"><Loader2 className="h-6 w-6 text-amber-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-24 border border-dashed border-gray-800 rounded-2xl">
          <div className="h-14 w-14 rounded-2xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center mx-auto"><Users className="h-7 w-7 text-amber-400" /></div>
          <p className="mt-4 text-white font-medium">No accounts here</p>
        </div>
      ) : (
        <div className="bg-card border border-gray-800 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-gray-500 text-xs uppercase tracking-wide border-b border-gray-800">
                <th className="px-5 py-3 font-medium w-10">
                  <input data-testid="select-all-accounts" type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="h-4 w-4 accent-amber-500 cursor-pointer" title="Select all" />
                </th>
                <th className="px-5 py-3 font-medium">Account</th>
                <th className="px-5 py-3 font-medium">Organization</th>
                <th className="px-5 py-3 font-medium">Heard From</th>
                <th className="px-5 py-3 font-medium">Plan</th>
                <th className="px-5 py-3 font-medium">Role</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Joined</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr></thead>
              <tbody>
                {filtered.map((u, i) => {
                  const st = STATUS[u.status] || STATUS.approved;
                  return (
                    <motion.tr key={u.user_id} data-testid={`user-row-${u.user_id}`} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: Math.min(i * 0.03, 0.3) }} className={"border-t border-gray-800/60 hover:bg-gray-800/30 " + (selected.has(u.user_id) ? "bg-amber-500/[0.04]" : "")}>
                      <td className="px-5 py-3">
                        {u.role === "admin" ? <span className="text-gray-700">—</span> : (
                          <input data-testid={`select-${u.user_id}`} type="checkbox" checked={selected.has(u.user_id)} onChange={() => toggleSel(u.user_id)} className="h-4 w-4 accent-amber-500 cursor-pointer" />
                        )}
                      </td>
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {u.picture ? <img src={u.picture} alt="" className="h-8 w-8 rounded-full object-cover" /> : <div className="h-8 w-8 rounded-full gold-gradient flex items-center justify-center text-xs font-bold text-black">{(u.name || "U")[0]}</div>}
                          <div><p className="text-white font-medium">{u.name || "—"}</p><p className="text-xs text-gray-500">{u.email}</p>{u.phone && <p data-testid={`phone-${u.user_id}`} className="text-xs text-gray-500 flex items-center gap-1 mt-0.5"><Phone className="h-3 w-3 text-gray-600" />{u.phone}</p>}</div>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-300"><span className="inline-flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-gray-500" />{u.organization || "—"}</span></td>
                      <td className="px-5 py-3 text-gray-400"><span data-testid={`heard-from-${u.user_id}`} className="text-xs">{u.heard_from || "—"}</span></td>
                      <td className="px-5 py-3">
                        {u.role === "admin" ? (
                          <span className="text-gray-600 text-xs">—</span>
                        ) : (
                          <div className="flex flex-col gap-1.5">
                            {u.pending_payment_plan !== undefined && u.pending_payment_plan !== null && u.pending_payment_plan !== "" && (
                              <span data-testid={`payment-claimed-${u.user_id}`} className="inline-flex items-center gap-1.5 w-max px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 animate-pulse">
                                <Wallet className="h-3 w-3" /> Paid — activate {PLANS[u.pending_payment_plan]?.label || ""}
                              </span>
                            )}
                            <div className="flex items-center gap-2">
                              <select
                                data-testid={`plan-select-${u.user_id}`}
                                value={u.plan || ""}
                                disabled={planBusy === u.user_id}
                                onChange={(e) => setPlan(u, e.target.value)}
                                className="bg-gray-900 border border-gray-700 text-xs text-white rounded-lg px-2 py-1.5 focus:outline-none focus:border-amber-500/60 hover:border-gray-600 transition-colors disabled:opacity-50 cursor-pointer"
                              >
                                <option value="">Free Trial</option>
                                <option value="basic-broadcaster">Basic — $49.95/mo</option>
                                <option value="media-ministry-hub">Media Hub — $59.95/mo</option>
                                <option value="premium-services">Premium — $89.95/mo</option>
                                <option value="ascend-ultra">Ascend Ultra — $99.95/mo</option>
                              </select>
                              {planBusy === u.user_id && <Loader2 className="h-3.5 w-3.5 animate-spin text-amber-400" />}
                            </div>
                            <PricingBadge u={u} onClick={() => setPriceUser(u)} />
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-3">{u.role === "admin" ? <span className="inline-flex items-center gap-1 text-amber-400 text-xs font-semibold"><ShieldCheck className="h-3.5 w-3.5" /> Admin</span> : <span className="text-gray-400 text-xs">Customer</span>}</td>
                      <td className="px-5 py-3"><span className={"inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border " + st.cls}><span className={"h-1.5 w-1.5 rounded-full " + st.dot} />{st.label}</span></td>
                      <td className="px-5 py-3 text-gray-500 text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-5 py-3">
                        <div className="flex items-center justify-end gap-2">
                          {u.role !== "admin" && <button data-testid={`view-client-${u.user_id}`} onClick={() => navigate(`/clients/${u.user_id}`)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-amber-300 hover:border-amber-500/40 text-xs font-medium transition-colors" title="View client profile"><ExternalLink className="h-3.5 w-3.5" /> View</button>}
                          {u.role !== "admin" && <button data-testid={`charge-client-${u.user_id}`} onClick={() => setChargeUser(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-amber-300 hover:border-amber-500/40 text-xs font-medium transition-colors" title="Charge a card over the phone" style={{ whiteSpace: "nowrap" }}><CreditCard className="h-3.5 w-3.5" /> Charge</button>}
                          {u.role !== "admin" && u.pricing_override !== "comp" && <button data-testid={`send-invoice-${u.user_id}`} onClick={() => setInvUser(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-amber-300 hover:border-amber-500/40 text-xs font-medium transition-colors" title="Email a Stripe invoice" style={{ whiteSpace: "nowrap" }}><Receipt className="h-3.5 w-3.5" /> Send Invoice</button>}
                          {u.role !== "admin" && <button data-testid={`edit-client-${u.user_id}`} onClick={() => openEdit(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-amber-300 hover:border-amber-500/40 text-xs font-medium transition-colors" title="Edit client"><Pencil className="h-3.5 w-3.5" /> Edit</button>}
                          <button data-testid={`reset-pw-${u.user_id}`} onClick={() => { setPwUser(u); setNewPw(""); setShowPw(false); }} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-indigo-500/15 text-indigo-300 border border-indigo-500/30 hover:bg-indigo-500/25 text-xs font-medium transition-colors" title="Reset password"><KeyRound className="h-3.5 w-3.5" /> Reset Password</button>
                          {u.role !== "admin" && (recBusy === u.user_id ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : (
                            u.archive_streams === false ? (
                              <button data-testid={`archive-off-${u.user_id}`} onClick={() => toggleRecording(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-400 border border-gray-700 hover:text-white text-xs font-medium transition-colors" title="Stream archiving is OFF — click to enable"><VideoOff className="h-3.5 w-3.5" /> No Recording</button>
                            ) : (
                              <button data-testid={`archive-on-${u.user_id}`} onClick={() => toggleRecording(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-medium transition-colors" title="Stream archiving is ON — click to disable"><Video className="h-3.5 w-3.5" /> Recording</button>
                            )
                          ))}
                          {u.role !== "admin" && (busy === u.user_id ? <Loader2 className="h-4 w-4 animate-spin text-amber-400" /> : (
                            <>
                              {u.status !== "approved" && <button data-testid={`approve-${u.user_id}`} onClick={() => setStatus(u, "approved")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 hover:bg-emerald-500/25 text-xs font-medium transition-colors"><Check className="h-3.5 w-3.5" /> Approve</button>}
                              {u.status !== "rejected" && <button data-testid={`reject-${u.user_id}`} onClick={() => setStatus(u, "rejected")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-xs font-medium transition-colors"><X className="h-3.5 w-3.5" /> Reject</button>}
                              {u.status !== "pending" && <button data-testid={`reset-${u.user_id}`} onClick={() => setStatus(u, "pending")} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800 text-gray-300 border border-gray-700 hover:text-white text-xs font-medium transition-colors"><RotateCcw className="h-3.5 w-3.5" /></button>}
                              {!u.is_admin && <button data-testid={`access-${u.user_id}`} onClick={() => accessAccount(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/15 text-amber-300 border border-amber-500/30 hover:bg-amber-500/25 text-xs font-medium transition-colors"><LogIn className="h-3.5 w-3.5" /> Access</button>}
                              <button data-testid={`delete-client-${u.user_id}`} onClick={() => setDelUser(u)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 text-xs font-medium transition-colors" title="Delete account"><Trash2 className="h-3.5 w-3.5" /> Delete</button>
                            </>
                          ))}
                        </div>
                      </td>
                    </motion.tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <Dialog open={!!pwUser} onOpenChange={(o) => { if (!o) { setPwUser(null); setNewPw(""); setShowPw(false); } }}>
        <DialogContent className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><KeyRound className="h-5 w-5 text-indigo-400" /> Reset Password</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Set a new password for <span className="text-gray-200 font-medium">{pwUser?.name || pwUser?.email}</span>. It's hashed with bcrypt and their active sessions are signed out.
            </DialogDescription>
          </DialogHeader>
          <div className="pt-2">
            <Label className="text-gray-300">New password</Label>
            <div className="relative mt-1.5">
              <Input
                type={showPw ? "text" : "password"}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !pwSaving) resetPassword(); }}
                placeholder="At least 8 characters"
                data-testid="reset-pw-input"
                className="bg-black/30 border-gray-800 pr-10"
                autoFocus
              />
              <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="reset-pw-toggle" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300">
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => { setPwUser(null); setNewPw(""); setShowPw(false); }} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button
              onClick={resetPassword}
              disabled={pwSaving || newPw.trim().length < 8}
              data-testid="reset-pw-save"
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-400 disabled:opacity-50"
            >
              {pwSaving && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Password
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create / Edit client */}
      <Dialog open={clientOpen} onOpenChange={(o) => { if (!o) setClientOpen(false); }}>
        <DialogContent className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              {editUser ? <Pencil className="h-5 w-5 text-amber-400" /> : <UserPlus className="h-5 w-5 text-amber-400" />}
              {editUser ? "Edit Client" : "Add Client"}
            </DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              {editUser ? <>Update contact and account details for <span className="text-gray-200 font-medium">{editUser.name || editUser.email}</span>.</> : "Create a new client account. They can sign in with the email and password you set."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            {!editUser && (
              <>
                <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Email</Label><Input data-testid="client-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="pastor@church.org" className="bg-black/30 border-gray-800" /></div>
                <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Password</Label><Input data-testid="client-password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Min 6 characters" className="bg-black/30 border-gray-800" /></div>
              </>
            )}
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Name</Label><Input data-testid="client-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Organization</Label><Input data-testid="client-organization" value={form.organization} onChange={(e) => setForm({ ...form, organization: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Phone</Label><Input data-testid="client-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Date Activated</Label><Input data-testid="client-date-activated" type="date" value={form.date_activated} onChange={(e) => setForm({ ...form, date_activated: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            <div className="space-y-1.5 col-span-2"><Label className="text-gray-300 text-xs">Address</Label><Input data-testid="client-address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            <div className="space-y-1.5"><Label className="text-gray-300 text-xs">City</Label><Input data-testid="client-city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label className="text-gray-300 text-xs">State</Label><Input data-testid="client-state" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="bg-black/30 border-gray-800" /></div>
              <div className="space-y-1.5"><Label className="text-gray-300 text-xs">Zip</Label><Input data-testid="client-zip" value={form.zip} onChange={(e) => setForm({ ...form, zip: e.target.value })} className="bg-black/30 border-gray-800" /></div>
            </div>
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setClientOpen(false)} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button onClick={saveClient} disabled={savingClient} data-testid="client-save" className="inline-flex items-center gap-2 rounded-lg gold-gradient px-4 py-2 text-sm font-semibold text-black hover:brightness-110 disabled:opacity-50">
              {savingClient && <Loader2 className="h-4 w-4 animate-spin" />} {editUser ? "Save Changes" : "Create Client"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {chargeUser && <ChargeClientDialog user={chargeUser} onClose={() => setChargeUser(null)} />}
      {invUser && <SendInvoiceDialog user={invUser} onClose={() => setInvUser(null)} />}
      {priceUser && <PricingOverrideDialog user={priceUser} onClose={() => setPriceUser(null)} onSaved={(p) => setUsers((list) => list.map((x) => (x.user_id === priceUser.user_id ? { ...x, ...p } : x)))} />}

      {/* Delete confirmation */}
      <Dialog open={!!delUser} onOpenChange={(o) => { if (!o) setDelUser(null); }}>
        <DialogContent className="bg-[#0E131F] border-gray-800 text-white sm:max-w-[440px]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2"><Trash2 className="h-5 w-5 text-red-400" /> Delete Account</DialogTitle>
            <DialogDescription className="text-gray-400 text-sm">
              Permanently delete <span className="text-gray-200 font-medium">{delUser?.name || delUser?.email}</span>? This removes their login and sign-in sessions. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="mt-6 flex justify-end gap-3">
            <button onClick={() => setDelUser(null)} className="rounded-lg px-4 py-2 text-sm text-gray-400 hover:text-white">Cancel</button>
            <button onClick={deleteClient} disabled={deleting} data-testid="confirm-delete-client" className="inline-flex items-center gap-2 rounded-lg bg-red-500 px-4 py-2 text-sm font-semibold text-white hover:bg-red-400 disabled:opacity-50">
              {deleting && <Loader2 className="h-4 w-4 animate-spin" />} Delete Account
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
